import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const ProvenanceGraph = ({ 
  selectedCredit, 
  onNodeSelect, 
  filters,
  className = '' 
}) => {
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panPosition, setPanPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const graphRef = useRef(null);

  // Mock graph data representing credit provenance
  const mockGraphData = {
    nodes: [
      {
        id: 'issuance-001',
        type: 'issuance',
        label: 'Credit Issuance',
        registry: 'Verra VCS',
        serialNumber: 'VCS-2024-001-12345',
        timestamp: '2024-01-15T10:30:00Z',
        amount: 1000,
        position: { x: 100, y: 200 },
        status: 'verified'
      },
      {
        id: 'transfer-001',
        type: 'transfer',
        label: 'Registry Transfer',
        from: 'Project Developer ABC',
        to: 'Carbon Trader XYZ',
        timestamp: '2024-02-20T14:15:00Z',
        amount: 1000,
        position: { x: 300, y: 200 },
        status: 'completed'
      },
      {
        id: 'tokenization-001',
        type: 'tokenization',
        label: 'Blockchain Tokenization',
        platform: 'Ethereum',
        contractAddress: '0x742d35Cc6634C0532925a3b8D',
        timestamp: '2024-03-10T09:45:00Z',
        amount: 500,
        position: { x: 500, y: 150 },
        status: 'active'
      },
      {
        id: 'split-001',
        type: 'split',
        label: 'Credit Split',
        originalAmount: 1000,
        splitAmounts: [500, 500],
        timestamp: '2024-03-10T09:30:00Z',
        position: { x: 500, y: 250 },
        status: 'completed'
      },
      {
        id: 'retirement-001',
        type: 'retirement',
        label: 'Corporate Retirement',
        company: 'Global Carbon Corp',
        claimType: 'VCMI Contribution',
        timestamp: '2024-09-15T16:20:00Z',
        amount: 500,
        position: { x: 700, y: 200 },
        status: 'retired'
      }
    ],
    edges: [
      { from: 'issuance-001', to: 'transfer-001', type: 'transfer' },
      { from: 'transfer-001', to: 'split-001', type: 'split' },
      { from: 'split-001', to: 'tokenization-001', type: 'tokenization' },
      { from: 'split-001', to: 'retirement-001', type: 'retirement' }
    ]
  };

  const getNodeColor = (node) => {
    switch (node?.type) {
      case 'issuance': return '#52B788';
      case 'transfer': return '#2D6A4F';
      case 'tokenization': return '#F77F00';
      case 'split': return '#1B4332';
      case 'retirement': return '#D62828';
      default: return '#6C757D';
    }
  };

  const getNodeIcon = (type) => {
    switch (type) {
      case 'issuance': return 'Plus';
      case 'transfer': return 'ArrowRight';
      case 'tokenization': return 'Link';
      case 'split': return 'GitBranch';
      case 'retirement': return 'Trash2';
      default: return 'Circle';
    }
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart({
      x: e?.clientX - panPosition?.x,
      y: e?.clientY - panPosition?.y
    });
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      setPanPosition({
        x: e?.clientX - dragStart?.x,
        y: e?.clientY - dragStart?.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleZoom = (delta) => {
    const newZoom = Math.max(0.5, Math.min(2, zoomLevel + delta));
    setZoomLevel(newZoom);
  };

  const handleNodeClick = (node) => {
    onNodeSelect(node);
  };

  const resetView = () => {
    setZoomLevel(1);
    setPanPosition({ x: 0, y: 0 });
  };

  useEffect(() => {
    const handleGlobalMouseMove = (e) => handleMouseMove(e);
    const handleGlobalMouseUp = () => handleMouseUp();

    if (isDragging) {
      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [isDragging, dragStart]);

  return (
    <div className={`relative bg-card border border-border rounded-lg overflow-hidden ${className}`}>
      {/* Graph Controls */}
      <div className="absolute top-4 right-4 z-10 flex items-center space-x-2">
        <button
          onClick={() => handleZoom(0.1)}
          className="p-2 bg-background border border-border rounded-lg hover:bg-muted transition-smooth"
          title="Zoom In"
        >
          <Icon name="ZoomIn" size={16} />
        </button>
        <button
          onClick={() => handleZoom(-0.1)}
          className="p-2 bg-background border border-border rounded-lg hover:bg-muted transition-smooth"
          title="Zoom Out"
        >
          <Icon name="ZoomOut" size={16} />
        </button>
        <button
          onClick={resetView}
          className="p-2 bg-background border border-border rounded-lg hover:bg-muted transition-smooth"
          title="Reset View"
        >
          <Icon name="RotateCcw" size={16} />
        </button>
      </div>
      {/* Graph Canvas */}
      <div
        ref={graphRef}
        className="w-full h-96 cursor-grab active:cursor-grabbing"
        onMouseDown={handleMouseDown}
        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
      >
        <svg
          width="100%"
          height="100%"
          viewBox="0 0 800 400"
          className="overflow-visible"
        >
          <g transform={`translate(${panPosition?.x}, ${panPosition?.y}) scale(${zoomLevel})`}>
            {/* Render Edges */}
            {mockGraphData?.edges?.map((edge, index) => {
              const fromNode = mockGraphData?.nodes?.find(n => n?.id === edge?.from);
              const toNode = mockGraphData?.nodes?.find(n => n?.id === edge?.to);
              
              if (!fromNode || !toNode) return null;

              return (
                <g key={`edge-${index}`}>
                  <line
                    x1={fromNode?.position?.x + 30}
                    y1={fromNode?.position?.y + 30}
                    x2={toNode?.position?.x + 30}
                    y2={toNode?.position?.y + 30}
                    stroke="#E9ECEF"
                    strokeWidth="2"
                    markerEnd="url(#arrowhead)"
                  />
                </g>
              );
            })}

            {/* Arrow Marker Definition */}
            <defs>
              <marker
                id="arrowhead"
                markerWidth="10"
                markerHeight="7"
                refX="9"
                refY="3.5"
                orient="auto"
              >
                <polygon
                  points="0 0, 10 3.5, 0 7"
                  fill="#E9ECEF"
                />
              </marker>
            </defs>

            {/* Render Nodes */}
            {mockGraphData?.nodes?.map((node) => (
              <g key={node?.id}>
                <circle
                  cx={node?.position?.x + 30}
                  cy={node?.position?.y + 30}
                  r="25"
                  fill={getNodeColor(node)}
                  stroke="#FFFFFF"
                  strokeWidth="3"
                  className="cursor-pointer hover:opacity-80 transition-smooth"
                  onClick={() => handleNodeClick(node)}
                />
                <text
                  x={node?.position?.x + 30}
                  y={node?.position?.y + 35}
                  textAnchor="middle"
                  fill="white"
                  fontSize="12"
                  fontWeight="500"
                  className="pointer-events-none"
                >
                  {node?.type === 'issuance' ? 'I' :
                   node?.type === 'transfer' ? 'T' :
                   node?.type === 'tokenization' ? 'B' :
                   node?.type === 'split' ? 'S' : 'R'}
                </text>
                <text
                  x={node?.position?.x + 30}
                  y={node?.position?.y + 75}
                  textAnchor="middle"
                  fill="#212529"
                  fontSize="10"
                  fontWeight="500"
                  className="pointer-events-none"
                >
                  {node?.label}
                </text>
              </g>
            ))}
          </g>
        </svg>
      </div>
      {/* Graph Legend */}
      <div className="absolute bottom-4 left-4 bg-background border border-border rounded-lg p-3">
        <div className="text-xs font-medium text-foreground mb-2">Legend</div>
        <div className="space-y-1">
          {[
            { type: 'issuance', label: 'Issuance', color: '#52B788' },
            { type: 'transfer', label: 'Transfer', color: '#2D6A4F' },
            { type: 'tokenization', label: 'Tokenization', color: '#F77F00' },
            { type: 'split', label: 'Split', color: '#1B4332' },
            { type: 'retirement', label: 'Retirement', color: '#D62828' }
          ]?.map((item) => (
            <div key={item?.type} className="flex items-center space-x-2">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: item?.color }}
              />
              <span className="text-xs text-muted-foreground">{item?.label}</span>
            </div>
          ))}
        </div>
      </div>
      {/* Zoom Level Indicator */}
      <div className="absolute bottom-4 right-4 bg-background border border-border rounded-lg px-2 py-1">
        <span className="text-xs text-muted-foreground">
          {Math.round(zoomLevel * 100)}%
        </span>
      </div>
    </div>
  );
};

export default ProvenanceGraph;